import {
  Button,
  Typography,
  Box,
  Table,
  TableBody,
  TableHead,
} from '@mui/material';
import { Link } from 'react-router-dom';
import { floatVal, Nullable } from '@jamesgmarks/utilities';
import { toDollarAmount } from '../../../../app-utils';
import { StyledTableCell, StyledTableRow } from '../../../parts/mui/StyledTables';
import { ICreditNoteSplit } from './types';
import { useFormatBillingAddress } from './utils';
import { IClientDropdownItem } from '../../../../entity-interfaces/IClientDropdownItem';

interface SplitsReviewModeProps {
  creditNoteSplits: ICreditNoteSplit[];
  selectedClient: Nullable<IClientDropdownItem>;
  isSubmitting: boolean;
  onBackToReview: () => void;
  onConfirmSplits: () => void;
}

export const SplitsReviewMode = ({
  creditNoteSplits,
  selectedClient,
  isSubmitting,
  onBackToReview,
  onConfirmSplits,
}: SplitsReviewModeProps) => {
  const formatBillingAddress = useFormatBillingAddress();

  if (!creditNoteSplits || !Array.isArray(creditNoteSplits) || creditNoteSplits.length === 0) {
    return (
      <Typography variant="h6" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
        No credit note splits available.
      </Typography>
    );
  }

  const totalCredits = creditNoteSplits.reduce((sum, split) => sum + split.summary.creditCount, 0);
  const totalAmount = creditNoteSplits.reduce((sum, split) => sum + (floatVal(split.summary.totalAmount) ?? 0), 0);
  const totalSubtotal = creditNoteSplits.reduce((sum, split) => sum + (floatVal(split.summary.subtotalAmount) ?? 0), 0);
  const totalTax = creditNoteSplits.reduce((sum, split) => sum + (floatVal(split.summary.taxAmount) ?? 0), 0);
  const creditNoteComment = creditNoteSplits.find((split) => (split.notes ?? []).length > 0)?.notes?.[0];

  return (
    <>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Review how your {totalCredits} credits will be split into {creditNoteSplits.length} credit note(s)
        based on ownership group and tax rate. You can proceed to create the credit notes or go back to make changes.
      </Typography>

      <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>
          Split Summary
        </Typography>
        <Typography variant="body2">
          <strong>Client:</strong> {selectedClient?.name}
        </Typography>
        <Typography variant="body2">
          <strong>Total Credits:</strong> {totalCredits}
        </Typography>
        <Typography variant="body2">
          <strong>Credit Notes to Create:</strong> {creditNoteSplits.length}
        </Typography>
        <Typography variant="body2">
          <strong>Total Subtotal:</strong> {toDollarAmount(totalSubtotal)}
        </Typography>
        <Typography variant="body2">
          <strong>Total Tax:</strong> {toDollarAmount(totalTax)}
        </Typography>
        <Typography variant="body2">
          <strong>Total Amount:</strong> {toDollarAmount(totalAmount)}
        </Typography>
        {creditNoteComment && (
          <>
            <Typography variant="body2">
              <strong>Note:</strong> {creditNoteComment.note}
            </Typography>
            <Typography variant="body2">
              <strong>Display on PDF:</strong> {creditNoteComment.displayCommentOnPdf ? 'Yes' : 'No'}
            </Typography>
          </>
        )}
      </Box>

      {creditNoteSplits.map((split, index) => (
        <Box key={index} sx={{ mb: 3, p: 2, border: '1px solid', borderColor: 'grey.300', borderRadius: 1 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Credit Note #{index + 1}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Ownership Group ID: {split.ownershipGroupId} | Tax Rate: {split.taxRate} |
            Tax Name: {split.taxName || 'No Tax'}
          </Typography>

          {/* Billing Address Section */}
          <Box sx={{
            mb: 2,
            p: 1.5,
            bgcolor: 'blue.50',
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'blue.200',
          }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold', color: 'blue.800' }}>
              Billing Address:
            </Typography>
            <Typography variant="body2" sx={{ color: 'blue.700' }}>
              {formatBillingAddress(split.billingAddress)}
            </Typography>
          </Box>

          <Box sx={{ mb: 2, p: 1, bgcolor: 'grey.100', borderRadius: 1 }}>
            <Typography variant="body2">
              <strong>Credits:</strong> {split.summary.creditCount} |
              <strong> Subtotal:</strong> {toDollarAmount(floatVal(split.summary.subtotalAmount) ?? 0)} |
              <strong> Tax:</strong> {toDollarAmount(floatVal(split.summary.taxAmount) ?? 0)} |
              <strong> Total:</strong> {toDollarAmount(floatVal(split.summary.totalAmount) ?? 0)}
            </Typography>
          </Box>

          <Table size="small">
            <TableHead>
              <StyledTableRow>
                <StyledTableCell>Invoice #</StyledTableCell>
                <StyledTableCell>Charge ID</StyledTableCell>
                <StyledTableCell>Amount</StyledTableCell>
                <StyledTableCell>Description</StyledTableCell>
              </StyledTableRow>
            </TableHead>
            <TableBody>
              {split.credits.map((credit, creditIndex) => (
                <StyledTableRow key={creditIndex}>
                  {/* Prefer the denormalised invoiceNumber field; fall back to the nested invoice object, then 'N/A'. */}
                  <StyledTableCell>{credit.invoiceNumber ?? credit.invoice?.invoiceNumber ?? 'N/A'}</StyledTableCell>
                  <StyledTableCell>
                    <Link to={`/charges/${credit.generatedSubscriptionId}`} target="_blank">
                      {credit.generatedSubscriptionId}
                    </Link>
                  </StyledTableCell>
                  <StyledTableCell>{toDollarAmount(floatVal(credit.amount) ?? 0)}</StyledTableCell>
                  <StyledTableCell>{credit.description}</StyledTableCell>
                </StyledTableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      ))}

      <Box sx={{ mt: 3, textAlign: 'center' }}>
        <Button
          color="primary"
          variant="outlined"
          onClick={onBackToReview}
          sx={{ mr: 2 }}
        >
          Back to Edit Credits
        </Button>
        <Button
          color="success"
          variant="contained"
          onClick={onConfirmSplits}
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Creating Credit Notes...' : `Create ${creditNoteSplits.length} Draft Credit Notes`}
        </Button>
      </Box>
    </>
  );
};
